import moment from 'moment'
import { ORDER_STATUS } from './enums'

export const FormatShopifyImage = (image, size = 500) => {
    if (!image) return undefined

    let imageStr
    const imageArr = image.split('.jpg')
    imageStr = `${imageArr[0]}_${size}x.jpg`
    for (let i = 1; i < imageArr.length; i++) {
        imageStr += imageArr[i]
    }
    return imageStr
}

export const parseServiceSchedule = (schedule, options) => {
    // options.referenceMoment: moment object to evaluate service availability against
    const referenceMoment = options?.referenceMoment || moment()
    const currentDayName = referenceMoment.format('dddd')
    const activeDays = schedule.filter(day => day.times !== null)

    if (activeDays.length === 0) return { isService: true, servingTimes: [] }

    let isService = false
    const timeMap = new Map()

    for (const day of activeDays) {
        for (const time of day.times) {
            const timeKey = `${time.open}-${time.close}`

            // Check if the time interval already exists
            if (!timeMap.has(timeKey)) {
                timeMap.set(timeKey, {
                    open: time.open,
                    close: time.close,
                    days: [],
                })
            }
            timeMap.get(timeKey).days.push(day.name)

            // Check if reference time is within the operating hours
            if (day.name === currentDayName) {
                const [openHour, openMinute] = time.open.split(':').map(Number)
                const [closeHour, closeMinute] = time.close.split(':').map(Number)

                const openMoment = referenceMoment.clone().hour(openHour).minute(openMinute).second(0).millisecond(0)
                const closeMoment = referenceMoment.clone().hour(closeHour).minute(closeMinute).second(0).millisecond(0)

                if (referenceMoment.isBetween(openMoment, closeMoment)) {
                    isService = true
                }
            }
        }
    }

    const servingTimes = Array.from(timeMap.values())

    return {
        isService,
        servingTimes,
    }
}

export const extractInventoryIds = cartItems => {
    const ids = new Set()

    for (const item of cartItems) {
        item.variants?.forEach(v => ids.add(v.inventory?.id))
        item.selections?.forEach(sel => {
            sel.options?.forEach(opt => ids.add(opt.inventory?.id))
        })
    }

    return Array.from(ids)
}

export const processOrderStatus = order => {
    if (!order?.dishes) return { lineitems: [], totalQuantity: 0, totalAmount: 0 }

    // Use summary.finalAmount if available, otherwise calculate from dishes
    const totalAmount = order.summary?.finalAmount ?? null

    // group by dishId
    const dishGroups = new Map()

    order.dishes.forEach(dish => {
        if (!dishGroups.has(dish.dishId)) {
            dishGroups.set(dish.dishId, {
                dishId: dish.dishId,
                name: dish.name,
                image: dish.image,
                vendorName: dish.vendorName,
                stallName: dish.cafeStallName,
                pendingOrdersAhead: dish.pendingOrdersAhead,
                items: new Map(),
                completedCount: 0,
                preparingCount: 0,
            })
        }

        // create a unique key for each variant
        dish.variants.forEach(variant => {
            // sort and serialize selections to ensure the same selections will generate the same key
            const sortedSelections = [...dish.selections].sort((a, b) => a.id.localeCompare(b.id))

            const selectionsKey = JSON.stringify(sortedSelections)
            const variantKey = `${variant.variantId}-${selectionsKey}`
            const group = dishGroups.get(dish.dishId)

            if (!group.items.has(variantKey)) {
                group.items.set(variantKey, {
                    variant: {
                        ...variant,
                        quantity: 1,
                        completedCount: variant.completedAt ? 1 : 0,
                    },
                    selections: sortedSelections,
                })
            } else {
                const existingItem = group.items.get(variantKey)
                existingItem.variant.quantity += 1
                if (variant.completedAt) {
                    existingItem.variant.completedCount += 1
                }
            }
        })
    })

    // 2. Process each group, calculate status and quantity
    const items = Array.from(dishGroups.values()).map(group => {
        let totalCompleted = 0
        let totalPreparing = 0

        // Convert Map to array
        const mergedItems = Array.from(group.items.values()).map(item => {
            const { variant, selections } = item
            totalCompleted += variant.completedCount
            totalPreparing += variant.quantity - variant.completedCount

            return {
                variant,
                selections,
            }
        })

        // Determine status
        let status
        if (order.status === ORDER_STATUS.UNPAID.toUpperCase()) {
            status = ORDER_STATUS.UNPAID
        } else {
            status = totalPreparing === 0 ? ORDER_STATUS.COMPLETED : ORDER_STATUS.PAID
        }

        return {
            ...group,
            items: mergedItems,
            completedCount: totalCompleted,
            preparingCount: totalPreparing,
            status,
        }
    })

    // 3. Calculate total quantity and amount
    const quantity = items.reduce((sum, group) => {
        return (
            sum +
            group.items.reduce((groupSum, item) => {
                return groupSum + item.variant.quantity
            }, 0)
        )
    }, 0)

    const amount = items.reduce((sum, group) => {
        return (
            sum +
            group.items.reduce((groupSum, item) => {
                const selectionPrice = item.selections.reduce((acc, selection) => {
                    const selectedOptions = selection.options
                    return (
                        acc + selectedOptions.reduce((optionTotal, option) => optionTotal + option.sellPrice * option.quantity, 0)
                    )
                }, 0)

                return groupSum + (item.variant.sellPrice + selectionPrice) * item.variant.quantity
            }, 0)
        )
    }, 0)

    return {
        lineitems: items,
        totalQuantity: quantity,
        totalAmount: totalAmount !== null ? totalAmount : amount,
        summary: order.summary,
    }
}

/**
 * Parse group ID from joinedGroupName
 * @param {string} joinedGroupName - Group name in format 'pos-{type}-{id}' or 'pos-{id}'
 * @returns {Object} Object containing numericId (string), formattedId (string), and type (string|null)
 * @property {string|null} numericId - The numeric part of the ID as a string (e.g., "123" from "CASH_REGISTER-123")
 * @property {string|null} formattedId - The full ID without 'pos-' prefix (e.g., "CASH_REGISTER-123")
 * @property {string|null} type - The type part before the numeric ID (e.g., "CASH_REGISTER")
 */
export const parseGroupId = joinedGroupName => {
    if (!joinedGroupName || typeof joinedGroupName !== 'string') {
        return { numericId: null, formattedId: null, type: null }
    }

    // Remove 'pos-' prefix if present
    const withoutPrefix = joinedGroupName.startsWith('pos-') ? joinedGroupName.slice(4) : joinedGroupName

    // Handle edge case: empty string after removing prefix
    if (!withoutPrefix) {
        return { numericId: null, formattedId: null, type: null }
    }

    // Extract numeric ID (last part after last dash) - returns as string
    const parts = withoutPrefix.split('-')
    const numericId = parts.length > 1 ? parts[parts.length - 1] : withoutPrefix

    // Validate numericId is not empty
    if (!numericId) {
        return { numericId: null, formattedId: withoutPrefix, type: null }
    }

    return {
        numericId,
        formattedId: withoutPrefix,
        type: parts.length > 1 ? parts.slice(0, -1).join('-') : null,
    }
}

/**
 * Find matching group in cafeGroups by joinedGroupName
 * @param {string} joinedGroupName - Group name in format 'pos-{type}-{id}' or 'pos-{id}'
 * @param {Array} cafeGroups - Array of group objects with id, name, type properties
 * @returns {Object|null} Matching group object or null
 *
 * @description
 * Matching strategy:
 * - group.id actual format can be:
 *   1. Pure numeric string (e.g., "123")
 *   2. Formatted string with type prefix (e.g., "CASH_REGISTER-123")
 *   3. Numeric value that will be converted to string
 *
 * The function tries to match in the following order:
 * 1. Match numeric ID part (e.g., "123" from "CASH_REGISTER-123")
 * 2. Match full formatted ID (e.g., "CASH_REGISTER-123")
 * 3. Match with 'pos-' prefix (e.g., "pos-CASH_REGISTER-123")
 */
export const findGroupByJoinedName = (joinedGroupName, cafeGroups) => {
    if (!joinedGroupName || !Array.isArray(cafeGroups) || cafeGroups.length === 0) {
        return null
    }

    const { numericId, formattedId } = parseGroupId(joinedGroupName)

    // Try to find matching group
    // group.id actual format: can be numeric string ("123"), formatted string ("CASH_REGISTER-123"), or numeric value
    const match = cafeGroups.find(group => {
        const gid = String(group.id)
        return (
            gid === String(numericId) || // Match numeric ID (e.g., "123")
            gid === String(formattedId) || // Match formatted ID (e.g., "CASH_REGISTER-123")
            `pos-${gid}` === joinedGroupName // Match with pos- prefix (e.g., if gid is "123", "pos-123" === "pos-CASH_REGISTER-123" won't match, but if gid is "CASH_REGISTER-123", "pos-CASH_REGISTER-123" === joinedGroupName will match)
        )
    })

    return match || null
}
